import { CustomTableSortItems } from 'commons/components/table/sort/custom-table-sorting.model';
import { useTranslation } from 'core/i18n/i18n';
import { SettingsFullWidthContentLayout } from 'modules/settings/components/settings-fullwidth-content-layout/settings-fullwidth-content-layout.component';
import { useCallback, useMemo, useState } from 'react';
import { usePopupManager } from 'react-popup-manager';
import { RestrictLocationsContainer } from './components/restrict-locations';
import { TenantUsersBulkActionsComponent } from './components/tenant-users-bulk-actions';
import { TenantUsersRowActionsComponent } from './components/tenant-users-row-actions';
import { useTenantUsersTableConfig } from './hooks/use-tenant-users-table-config.hook';
import { TenantUserRow } from './settings-user-location-assignment.vm';
import { SettingsUserLocationAssignmentListComponent } from './settings-user-location-assignment-list';
import { sortTenantUsers } from './settings-user-location-assignment.business';
import { DEFAULT_SORT, USER_LOCATION_ASSIGNMENT_I18N_PREFIX } from './settings-user-location-assignment.constants';
import { useTenantUsersListQuery } from './service/tenant-users/hooks/use-tenant-users-list.hook';

export const SettingsUserLocationAssignmentContainer: React.FC = () => {
    const { t } = useTranslation();
    const { users, isLoading, isDegraded, hasFailedToLoad, refetch } = useTenantUsersListQuery();
    const popupManager = usePopupManager();
    const config = useTenantUsersTableConfig();
    const [sortItems, setSortItems] = useState<CustomTableSortItems>(DEFAULT_SORT);
    const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(() => new Set());

    const sortedUsers = useMemo(() => sortTenantUsers(users, sortItems), [users, sortItems]);

    const clearSelection = useCallback(() => setSelectedRowIds(new Set()), []);

    // The page — not the popup — owns the refresh, so the summary still converges after the dialog has
    // torn itself down. Selection goes with it, or the action bar lingers after the success toast.
    const handleAssignmentSaved = useCallback(() => {
        void refetch();
        clearSelection();
    }, [refetch, clearSelection]);

    const openLocationsDialog = useCallback(
        (targets: TenantUserRow[]) => {
            if (targets.length === 0) {
                return;
            }

            popupManager.open(RestrictLocationsContainer, {
                users: targets.map((user) => ({ id: user.id, fullName: user.fullName })),
                onAssignmentSaved: handleAssignmentSaved,
            });
        },
        [popupManager, handleAssignmentSaved]
    );

    const handleClickRow = useCallback(
        (userId: string) => openLocationsDialog(users.filter((candidate) => candidate.id === userId)),
        [users, openLocationsDialog]
    );

    const handleSelectRow = useCallback((userId: string, selected: boolean) => {
        setSelectedRowIds((previous) => {
            const next = new Set(previous);

            if (selected) {
                next.add(userId);
            } else {
                next.delete(userId);
            }

            return next;
        });
    }, []);

    const renderRowOptions = useCallback(
        (user: TenantUserRow) => [
            <TenantUsersRowActionsComponent
                key={`tenant-users-row-actions-${user.id}`}
                userId={user.id}
                userName={user.fullName}
                onEditLocations={handleClickRow}
            />,
        ],
        [handleClickRow]
    );

    const handleBulkEditLocations = useCallback(
        () => openLocationsDialog(users.filter((user) => selectedRowIds.has(user.id))),
        [users, selectedRowIds, openLocationsDialog]
    );

    return (
        <SettingsFullWidthContentLayout
            title={t(`${USER_LOCATION_ASSIGNMENT_I18N_PREFIX}.title`)}
            description={t(`${USER_LOCATION_ASSIGNMENT_I18N_PREFIX}.subtitle`)}
            extraContent={
                <TenantUsersBulkActionsComponent
                    selectedCount={selectedRowIds.size}
                    onClearSelection={clearSelection}
                    onEditLocations={handleBulkEditLocations}
                />
            }
        >
            <SettingsUserLocationAssignmentListComponent
                users={sortedUsers}
                config={config}
                isLoading={isLoading}
                isDegraded={isDegraded}
                hasFailedToLoad={hasFailedToLoad}
                defaultSort={DEFAULT_SORT}
                onSortOrderChange={setSortItems}
                onRetry={() => void refetch()}
                onClickRow={handleClickRow}
                selectedRowIds={selectedRowIds}
                setSelectedRowIds={setSelectedRowIds}
                onSelectRow={handleSelectRow}
                rowOptions={renderRowOptions}
            />
        </SettingsFullWidthContentLayout>
    );
};
